import React from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { getCampaignProgress } from "../api/campaigns";

const POLL_MS = 5000;

/**
 * Live progress for a campaign that is sending (ESERV2-1425).
 *
 * A large send takes a long time — campaign 63's 23,536 recipients took about
 * 36 minutes — and until now the list simply said "In Progress" with no counts
 * and no refresh, so the only way to tell whether anything was happening was to
 * reload the page and hope. The campaign's own total_sent is not written until
 * the whole send finishes, which is why this reads the send log instead.
 *
 * When the send finishes it refreshes the campaign list once, so the row flips
 * to Sent on its own.
 */
const SendingProgress = ({ campaignId }: { campaignId: string }) => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.index";
  const queryClient = useQueryClient();

  const { data, isError } = useQuery(
    ["campaign-progress", campaignId],
    () => getCampaignProgress(campaignId),
    {
      // Stop polling once nothing is left queued.
      refetchInterval: (progress) => (progress && progress.complete ? false : POLL_MS),
      // react-query suspends interval refetching while the window is not
      // focused. A send takes tens of minutes and is exactly the kind of thing
      // left open on a second monitor, so without this the counts silently
      // freeze until the window is clicked - verified in a real browser, where
      // the endpoint was called once and then never again.
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      onSuccess: (progress) => {
        if (progress.complete) {
          queryClient.invalidateQueries("campaigns");
        }
      },
    }
  );

  // Never let a progress hiccup hide the fact that the campaign is sending.
  if (isError || !data || data.total === 0) {
    return <span className="text-sm text-gray-400">{t(`${tBase}.inProgress`)}</span>;
  }

  // Every row that is no longer queued is done. SES can report a bounce while
  // the send is still running, and counting only sent+failed left those rows
  // out: the bar stalled short of the end and the count could sit below a
  // figure it had already shown.
  const done = data.sent + data.failed + data.bounced + data.complained;

  return (
    <span className="text-sm text-gray-500" title={t(`${tBase}.inProgress`)}>
      {t(`${tBase}.sendingProgress`, {
        done: done.toLocaleString(),
        total: data.total.toLocaleString(),
      })}
    </span>
  );
};

export default SendingProgress;
